/**
 * REST API v1 client (watched, recommendations, preferences).
 */

import { error as logError } from '../utils/logger';
import { apiUrl } from '../utils/apiUrl';

const WATCHED_ARRAY_KEYS = {
  movie: 'movies',
  series: 'series',
  music: 'music'
};

const WATCHED_FILENAMES = {
  movie: 'movies_watched.json',
  series: 'series_watched.json',
  music: 'music_listened.json'
};

const RECOMMENDATION_FILENAMES = {
  movie: 'movie_recommendations.json',
  series: 'series_recommendations.json',
  music: 'music_recommendations.json'
};

/** LLM generate can take 1–3 min (Alibaba 27b + large prompt). */
const GENERATE_REQUEST_TIMEOUT_MS = 200000;

/**
 * @param {string} path
 * @param {RequestInit} [options]
 * @param {{ timeoutMs?: number }} [config]
 */
async function request(path, options = {}, config = {}) {
  const headers = {
    ...(options.body ? { 'Content-Type': 'application/json' } : {}),
    ...(options.headers || {})
  };

  const signal = config.timeoutMs ? AbortSignal.timeout(config.timeoutMs) : undefined;

  let response;
  try {
    response = await fetch(apiUrl(path), {
      ...options,
      headers,
      credentials: 'include',
      signal
    });
  } catch (err) {
    if (err.name === 'TimeoutError' || err.name === 'AbortError') {
      const timeoutError = new Error('GENERATE_TIMEOUT');
      timeoutError.code = 'CLIENT_TIMEOUT';
      throw timeoutError;
    }
    throw err;
  }

  if (!response.ok) {
    const errorText = await response.text();
    const error = new Error(`HTTP ${response.status}: ${errorText}`);
    error.status = response.status;
    throw error;
  }

  if (response.status === 204) return null;

  const text = await response.text();
  if (!text || !text.trim()) {
    const emptyError = new Error('Empty response body from API');
    emptyError.code = 'EMPTY_RESPONSE';
    throw emptyError;
  }
  try {
    return JSON.parse(text);
  } catch (parseErr) {
    const err = new Error(`Invalid JSON response: ${text.slice(0, 120)}`);
    err.code = 'INVALID_JSON';
    throw err;
  }
}

function buildQuery(params) {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      q.set(key, String(value));
    }
  });
  const s = q.toString();
  return s ? `?${s}` : '';
}

export const DEFAULT_LIST_PAGE_SIZE = 24;
export const SEARCH_LIST_LIMIT = 2000;

export const apiV1 = {
  /** @returns {Promise<{ items: object[], total: number, limit: number, offset: number }>} */
  async listWatched(type, { limit = 5000, offset = 0, sort } = {}) {
    return request(`/api/v1/watched/${type}${buildQuery({ limit, offset, sort })}`);
  },

  async getWatched(type, contentId) {
    const data = await request(`/api/v1/watched/${type}/${encodeURIComponent(contentId)}`);
    return data.item;
  },

  async upsertWatched(type, item) {
    return request(`/api/v1/watched/${type}`, {
      method: 'POST',
      body: JSON.stringify({ item })
    });
  },

  async patchWatched(type, contentId, patch) {
    const data = await request(
      `/api/v1/watched/${type}/${encodeURIComponent(contentId)}`,
      { method: 'PATCH', body: JSON.stringify(patch) }
    );
    return data.item;
  },

  async deleteWatched(type, contentId) {
    return request(`/api/v1/watched/${type}/${encodeURIComponent(contentId)}`, {
      method: 'DELETE'
    });
  },

  /** @returns {Promise<{ items: object[], total: number, limit: number, offset: number, status?: string }>} */
  async listRecommendations(type, { limit = 5000, offset = 0, status = 'all', sort } = {}) {
    return request(`/api/v1/recommendations/${type}${buildQuery({ limit, offset, status, sort })}`);
  },

  async getRecommendation(type, contentId) {
    const data = await request(
      `/api/v1/recommendations/${type}/${encodeURIComponent(contentId)}`
    );
    return data.item;
  },

  async upsertRecommendation(type, item) {
    return request(`/api/v1/recommendations/${type}`, {
      method: 'POST',
      body: JSON.stringify({ item })
    });
  },

  async upsertRecommendationsBatch(type, items) {
    return request(`/api/v1/recommendations/${type}/batch`, {
      method: 'POST',
      body: JSON.stringify({ items })
    });
  },

  async patchRecommendation(type, contentId, patch) {
    const data = await request(
      `/api/v1/recommendations/${type}/${encodeURIComponent(contentId)}`,
      { method: 'PATCH', body: JSON.stringify(patch) }
    );
    return data.item;
  },

  async deleteRecommendation(type, contentId) {
    return request(
      `/api/v1/recommendations/${type}/${encodeURIComponent(contentId)}`,
      { method: 'DELETE' }
    );
  },

  async getPreferences() {
    const data = await request('/api/v1/preferences');
    return data.preferences || {};
  },

  async patchPreferences(partial) {
    const data = await request('/api/v1/preferences', {
      method: 'PATCH',
      body: JSON.stringify(partial)
    });
    return data.preferences;
  },

  /**
   * Server-side recommendation generation (Phase 2).
   * @param {object} body - { contentType, count, force, recentlyRatedId, useCrossContentContext, language }
   */
  async generateRecommendations(body) {
    return request(
      '/api/v1/recommendations/generate',
      {
        method: 'POST',
        body: JSON.stringify(body)
      },
      { timeoutMs: GENERATE_REQUEST_TIMEOUT_MS }
    );
  }
};

/**
 * Load legacy blob shape via v1 list endpoints (for getCachedData compatibility).
 * @param {string} filename
 * @returns {Promise<object|null>}
 */
export async function loadResourceViaV1(filename) {
  const watchedType = Object.entries(WATCHED_FILENAMES).find(([, f]) => f === filename)?.[0];
  if (watchedType) {
    const { items } = await apiV1.listWatched(watchedType);
    return { [WATCHED_ARRAY_KEYS[watchedType]]: items };
  }

  const recType = Object.entries(RECOMMENDATION_FILENAMES).find(([, f]) => f === filename)?.[0];
  if (recType) {
    const { items } = await apiV1.listRecommendations(recType, { status: 'all' });
    return { recommendations: items };
  }

  if (filename === 'user_preferences.json') {
    const preferences = await apiV1.getPreferences();
    return preferences;
  }

  return null;
}

export async function isApiV1Available() {
  try {
    await apiV1.getPreferences();
    return true;
  } catch (e) {
    logError('API v1 not available, falling back to legacy', e);
    return false;
  }
}

export { WATCHED_FILENAMES, RECOMMENDATION_FILENAMES, WATCHED_ARRAY_KEYS };
